import { z } from "zod";
export const InputModeSchema = z.enum(["WITH_JD", "WITHOUT_JD", "JD_ONLY"]);
export const RequirementLevelSchema = z.enum(["CORE", "IMPORTANT", "BONUS"]);
export const EvidenceStrengthSchema = z.enum(["STRONG", "PARTIAL", "NONE"]);
export const RelevanceSchema = z.enum(["HIGH", "PARTIAL", "LOW"]);
export const MatchLevelSchema = z.enum(["HIGH", "MEDIUM", "LOW"]);
export const RequirementSchema = z.object({
    id: z.string().describe("Stable short id, for example req_1"),
    title: z.string().describe("Concise requirement title"),
    detail: z.string().describe("What the work actually requires"),
    level: RequirementLevelSchema,
});
export const IssueSchema = z.object({
    id: z.string().describe("Stable short id, for example issue_1"),
    title: z.string().max(24),
    section: z.string().describe("Resume experience or section this issue belongs to"),
    reason: z.string().max(180),
    direction: z.string().max(220).describe("Direction only; never write replacement resume copy"),
    example: z.string().max(360).optional().default("").describe("A truthful example of a stronger expression or an interview question; never invent facts"),
    priority: z.number().int().min(1),
    sourceQuotes: z.array(z.string()).describe("Short verbatim evidence quotes from the resume; empty only when the issue is an omission"),
});
export const AiAnalysisSchema = z.object({
    inputMode: InputModeSchema,
    candidate: z.object({
        name: z.string().nullable(),
        inferredType: z.enum(["GRADUATE", "EARLY_CAREER", "UNCLEAR"]),
    }),
    job: z
        .object({
        company: z.string().nullable(),
        title: z.string().nullable(),
        essence: z.string(),
        requirements: z.array(RequirementSchema),
        hardConditions: z.array(z.object({
            condition: z.string(),
            resumeEvidence: z.string().nullable(),
            status: z.enum(["MET", "UNCLEAR", "GAP"]),
        })),
    })
        .nullable(),
    experiences: z.array(z.object({
        name: z.string(),
        kind: z.enum(["WORK", "INTERNSHIP", "PROJECT", "CAMPUS", "EDUCATION", "SKILL", "OTHER"]),
        relevance: RelevanceSchema,
        reason: z.string(),
    })),
    requirementEvidence: z.array(z.object({
        requirementId: z.string(),
        strength: EvidenceStrengthSchema,
        explanation: z.string(),
        sourceQuotes: z.array(z.string()),
    })),
    issues: z.array(IssueSchema).min(3),
    topIssueIds: z.array(z.string()).length(3),
    report: z.object({
        headline: z.string().max(120),
        jobOverview: z.string().nullable(),
        importantWork: z.array(z.string()),
        keyAbilities: z.array(z.string()),
        bonusRequirements: z.array(z.string()),
        strengths: z.array(z.string()),
        gaps: z.array(z.string()),
        reductionAdvice: z.array(z.string()),
        overallAdvice: z.string(),
    }),
    warnings: z.array(z.string()),
});
export const PublicReportSchema = z.object({
    id: z.string(),
    title: z.string(),
    createdAt: z.string(),
    expiresAt: z.string(),
    inputMode: InputModeSchema,
    company: z.string().nullable(),
    jobTitle: z.string().nullable(),
    matchingLevel: MatchLevelSchema.nullable(),
    headline: z.string(),
    jobOverview: z.string().nullable(),
    importantWork: z.array(z.string()),
    keyAbilities: z.array(z.string()),
    bonusRequirements: z.array(z.string()),
    topIssues: z.array(IssueSchema),
    strengths: z.array(z.string()),
    gaps: z.array(z.string()),
    issues: z.array(IssueSchema),
    hardConditions: z.array(z.object({
        condition: z.string(),
        resumeEvidence: z.string().nullable(),
        status: z.enum(["MET", "UNCLEAR", "GAP"]),
    })),
    reductionAdvice: z.array(z.string()),
    overallAdvice: z.string(),
    warnings: z.array(z.string()),
    disclaimer: z.string(),
    feedbackSubmitted: z.boolean(),
});
export const TaskStatusSchema = z.enum([
    "QUEUED",
    "READING_RESUME",
    "READING_JD",
    "ANALYZING",
    "GENERATING_REPORT",
    "COMPLETED",
    "FAILED",
]);
export const TaskFailureCodeSchema = z.enum([
    "AI_CONFIGURATION", "TASK_TIMEOUT", "RETRY_EXHAUSTED", "STORAGE_UNAVAILABLE", "ENTITLEMENT_MISSING",
    "OCR_CONFIGURATION", "OCR_TIMEOUT", "OCR_RATE_LIMIT", "OCR_UNAVAILABLE",
    "RESOURCE_LIMIT",
    "PARSER_TIMEOUT",
    "PARSER_BUSY",
    "PARSER_UNAVAILABLE",
    "FILE_UNSUPPORTED",
    "FILE_TOO_LARGE",
    "TOO_MANY_IMAGES",
    "FILE_CORRUPTED",
    "PDF_ENCRYPTED",
    "CONTENT_EMPTY",
    "RESUME_INCOMPLETE",
    "JD_INCOMPLETE",
    "NOT_A_RESUME",
    "AI_RATE_LIMIT",
    "AI_TIMEOUT",
    "AI_UNAVAILABLE",
    "AI_REFUSAL",
    "AI_FORMAT",
    "SAVE_FAILED",
    "CREDITS_EXHAUSTED",
    "TASK_CANCELLED",
    "TASK_DELETED",
    "UNKNOWN",
]);
export const CreateTaskSchema = z.object({
    resumeMaterialId: z.string().uuid().nullable(),
    jdMaterialId: z.string().uuid().nullable(),
    idempotencyKey: z.string().min(8).max(100),
    consentVersion: z.string().min(1).max(40),
}).refine((value) => Boolean(value.resumeMaterialId || value.jdMaterialId), {
    message: "At least one material is required",
    path: ["resumeMaterialId"],
});
export const PasteMaterialSchema = z.object({
    kind: z.enum(["RESUME", "JD"]),
    text: z.string().max(30_000),
});
export const FeedbackSchema = z.object({
    helpful: z.boolean(),
    reasons: z.array(z.enum(["JOB_MISUNDERSTOOD", "MISSED_REAL_PROBLEM", "NOT_SPECIFIC", "CONTENT_MISREAD", "OTHER"])).max(5),
    comment: z.string().max(300),
});
//# sourceMappingURL=schemas.js.map